import React, { useContext } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Modal } from 'design-system/src/components/Modal';
import { StyledModalBody } from '../styles';

import { StandardErrorToast, Toast } from 'design-system/src/components/Toast/Toast';
import { EhrIntegrationForm } from '../EhrIntegrationForm';
import { AuthContext } from '../../../auth';
import { SupportedEhrName } from '../../../types';
import { getEhrIntegrationConfig } from '../../tasks/EhrIntegrationConfig';
import { client } from '../../../api';
import * as Sentry from '@sentry/react';
import styled from 'styled-components';

type EhrCredentials = Record<string, string>;

const FormModalBody = styled(StyledModalBody)`
  height: auto;
  max-height: 80vh;
  overflow-y: auto;
  padding-bottom: 32px;
`;

interface EhrIntegrationModalProps {
  open: boolean;
  onClose: () => void;
  ehr: SupportedEhrName;
}

const EhrIntegrationModal: React.FC<EhrIntegrationModalProps> = ({ open, onClose, ehr }) => {
  const { user } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const practiceName = user?.practice_name || '';

  if (!practiceName) {
    Sentry.captureException(new Error('Practice name is missing from user context'));
  }

  const config = getEhrIntegrationConfig(ehr, practiceName);

  const submit = useMutation<void, Error, EhrCredentials>({
    mutationFn: async (creds: EhrCredentials) => {
      await client.post('/onboarding/ehr-integration', {
        ehr_name: ehr,
        credentials: creds,
        source: 'onboarding_flow',
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['onboardingInfo'] });
      toast.custom(() => <Toast variant="success">{config.getSuccessMessage()}</Toast>);
      onClose();
    },
    onError: (error) => {
      console.error(error);
      toast.custom(() => <StandardErrorToast />);
    },
  });

  const handleSubmit = (credentials: EhrCredentials) => {
    submit.mutate(credentials);
  };

  return (
    <Modal open={open} onClose={onClose} wide>
      <FormModalBody>
        <EhrIntegrationForm
          ehrType={ehr}
          practiceName={practiceName}
          onSubmit={handleSubmit}
          isSubmitLoading={submit.isPending}
          source="onboardingFlow"
        />
      </FormModalBody>
    </Modal>
  );
};

export default EhrIntegrationModal;
